import { memo, useMemo } from "react";
import { TimeInputField } from "@/components/TimeInputField";
import {
  formatDateTime,
  formatNumber,
  formatTime,
} from "@/lib/utils/formatters";
import { sumColumnValues } from "@/lib/utils/sumColumnValues";
import { Module, PATH_TIMESHEET_ENTRIES_SHIFT_REPORTS } from "@/constants";
import { generatePath, useParams } from "react-router-dom";
import { ReadTimesheetEntryContractorVisit } from "@/types/timesheetEntryContractorVisit";

import { QueryTable } from "@/components/query-table/QueryTable";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FetchListRequest } from "@/services/api";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import PageHeader from "@/components/PageHeader";
import { TimesheetEntriesContractorsVisitsForm } from "./TimesheetEntriesContractorsVisitsForm";
import { TimesheetEntriesContractorsVisitsService } from "@/modules/timesheet-entries/services/timesheetEntriesContractorsVisits";
import { useTimesheetEntriesBreadcrumb } from "../hooks/useTimesheetEntriesBreadcrumb";
import { formTimesheetEntryContractorVisit } from "../schema";
import { FilterType } from "@/lib/filters";
import { useTimesheetShiftReportQuery } from "../hooks/useTimesheetShiftReportQuery";
import { DisplayTimesheetEntryContractorVisit } from "../types";
import { TimesheetHeader } from "./TimesheetHeader";
import { ShiftDateTimePickerField } from "./ShiftDateTimePickerField";

export const TimesheetEntriesContractorsVisitsDirectory = memo(() => {
  const name = "timesheetEntriesContractorsVisits";
  const { userId } = useParams();
  const shiftReportQuery = useTimesheetShiftReportQuery();

  const options =
    useCreateDataTableOptions<DisplayTimesheetEntryContractorVisit>(name);

  const queryKey = useDynamicQueryParams(name, options);

  const fetchList = (
    fetchParams: FetchListRequest<ReadTimesheetEntryContractorVisit>,
  ) => {
    const finalFetchParams = { ...fetchParams };

    if (userId) {
      Object.assign(finalFetchParams, {
        filters: [
          ...(fetchParams.filters ?? []),
          {
            id: "contractor_id",
            value: {
              value: userId,
              filter: FilterType.EqualsString,
            },
          },
        ],
      });
    }

    return TimesheetEntriesContractorsVisitsService.getList(finalFetchParams);
  };

  const listQuery = useQuery({
    queryKey,
    queryFn: () =>
      fetchList({
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(
    () => ({
      query: listQuery,
      queryKey,
    }),
    [listQuery, queryKey],
  );

  const breadcrumbItems = useTimesheetEntriesBreadcrumb({
    shiftReportQuery,
    visitsListQuery: listQuery,
  });

  return (
    <>
      <QueryTable<DisplayTimesheetEntryContractorVisit>
        schema={formTimesheetEntryContractorVisit}
        name={name}
        query={listQuery}
        options={options}
        pathBuilder={(row) =>
          generatePath(PATH_TIMESHEET_ENTRIES_SHIFT_REPORTS, {
            shiftReportId: row.shift_report_id,
          })
        }
        tableFeatures={{
          groupBy: ["group"],
          enableGrouping: true,
          exportSettings: {
            moduleName: Module.TimesheetEntriesVisits,
          },
        }}
        columnOverrides={{
          store_name: {
            id: "store_name",
            aggregationFn: "count",
            aggregatedCell: TimesheetHeader,
          },
          group: {
            id: "group",
          },
          start_datetime: {
            id: "start_datetime",
            cell: ({ row }) =>
              row.original.edit_allowed ? (
                <ShiftDateTimePickerField row={row} field="start_datetime" />
              ) : (
                formatDateTime(row.original.start_datetime)
              ),
          },
          end_datetime: {
            id: "end_datetime",
            cell: ({ row }) =>
              row.original.edit_allowed ? (
                <ShiftDateTimePickerField row={row} field="end_datetime" />
              ) : (
                formatDateTime(row.original.end_datetime)
              ),
          },
          break_time: {
            id: "break_time",
            cell: ({ row }) =>
              row.original.edit_allowed ? (
                <TimeInputField
                  name={`entries.${row.original.id}.break_time`}
                />
              ) : (
                formatTime(row.original.break_time)
              ),
          },
          hours_total: {
            id: "hours_total",
            aggregationFn: (columnId, rows) => sumColumnValues(columnId, rows),
            aggregatedCell: ({ getValue }) => formatNumber(getValue(), ""),
          },
          break_hours: {
            id: "break_hours",
            aggregationFn: (columnId, rows) => sumColumnValues(columnId, rows),
            aggregatedCell: ({ getValue }) => formatNumber(getValue(), ""),
          },
          hours_without_break: {
            id: "hours_without_break",
            aggregationFn: (columnId, rows) => sumColumnValues(columnId, rows),
            aggregatedCell: ({ getValue }) => formatNumber(getValue(), ""),
          },
        }}
        translationKey="timesheetEntries"
      >
        <PageHeader breadcrumbItems={breadcrumbItems} />
        <DataTableQueryContextProvider value={queryValue}>
          <div className="flex flex-row gap-2 justify-between items-center py-2">
            <QueryTable.Menubar className="mb-0 grow">
              <QueryTable.Menubar.Tools></QueryTable.Menubar.Tools>
              <QueryTable.Menubar.Columns />
              <QueryTable.Menubar.Settings />
            </QueryTable.Menubar>
          </div>
          <TimesheetEntriesContractorsVisitsForm />
        </DataTableQueryContextProvider>
      </QueryTable>
    </>
  );
});
